/*
 * ====================================================================================
 * FILE: RentalDetailsPage.jsx
 * PURPOSE: A dedicated page for managing a specific Rental Contract's details.
 *
 * DESCRIPTION:
 * This page provides a comprehensive view of a single machine's rental assignment.
 * It combines three critical data sources:
 * 1. Machine Identity: What is the asset? (Brand, Model, Image)
 * 2. Contract Context: Who has it? (Customer, Site Location, Duration)
 * 3. Operational History: How is it being used? (Daily Logs, Hours, Fuel)
 *
 * KEY FEATURES:
 * - "Snapshot" Header: A unified card showing machine, customer, and date details.
 * - Contract Access: Quick links to view the full PDF contract or download data.
 * - Daily Usage Logs: A computed list of daily activity derived from telemetry.
 *
 * DATA SOURCES:
 * - rentalService.getRentalDetail(id) -> Unified info by Equipment ID
 * - rentalService.getRentalHistory(id) -> Unified logs by Equipment ID
 *
 * AUTHOR: Development Team
 * DATE: 2024
 * ====================================================================================
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getContractOrigin } from '../../utils/contractOrigin';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    Chip,
    Stack,
    Divider,
    Breadcrumbs,
    Link as MuiLink,
    Avatar,
    IconButton,
    Skeleton,
    Tooltip,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert
} from '@mui/material';
import rentalService from '../../services/rentalService';
import geofenceService from '../../services/geofenceService';
import apiClient from '../../api/client';
import {
    ChevronRight,
    ChevronLeft,
    Person,
    LocationOn,
    Description,
    AccessTime,
    LocalGasStation,
    Speed,
    CalendarToday,
    ArrowForward,
    PictureAsPdf,
    TableChart,
    Event,
    WarningAmber,
    CheckCircleOutline,
    Map as MapIcon,
    Search,
    Close,
    Download as DownloadIcon
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, Polygon } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default Leaflet marker icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const RentalDetailsPage = () => {
    // --- 1. State & Hooks Configuration ---
    const navigate = useNavigate();
    const origin = getContractOrigin();   // the door this contract was opened from
    const { rentalId } = useParams(); // URL parameter (Equipment ID, e.g. EQ-B001)
    const [searchParams] = useSearchParams();
    const contractId = searchParams.get('contractId');

    const [loading, setLoading] = useState(true);
    const [rentalData, setRentalData] = useState(null); // Stores merged view model
    // Daily stats are fetched ONE MONTH at a time (the page used to compute
    // every day since contract start and crawled). Cache: 'YYYY-MM' -> rows.
    const [monthCache, setMonthCache] = useState({});
    const [monthLoading, setMonthLoading] = useState(false);
    const [manualByDate, setManualByDate] = useState(null);   // Map isoDate -> manual log
    const [contractStartIso, setContractStartIso] = useState(null); // 'YYYY-MM-DD' or null
    const [searchQuery, setSearchQuery] = useState(''); // Search query for filtering logs
    const [isSearchOpen, setIsSearchOpen] = useState(false); // Toggle search input visibility
    const [cityName, setCityName] = useState(''); // Stores the reverse geocoded city name

    // Month selector for the usage logs (default current month)
    const thisMonth = new Date().toLocaleDateString('en-CA').slice(0, 7); // YYYY-MM
    const [selectedMonth, setSelectedMonth] = useState(thisMonth);

    // Geofence polygon for this contract (to show the marked site on the map)
    const [geofence, setGeofence] = useState(null); // [{lat,lng}, ...] or null

    // Download dialog (per-machine PDF/Excel over a chosen date range)
    const [dlOpen, setDlOpen] = useState(false);
    const [dlFormat, setDlFormat] = useState('pdf');
    const [dlFrom, setDlFrom] = useState('');
    const [dlTo, setDlTo] = useState('');
    const [dlError, setDlError] = useState('');

    // Search runs over the months loaded so far (day name or date).
    const loadedLogs = Object.values(monthCache).flat();
    const filteredLogs = loadedLogs.filter(log => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            log.day.toLowerCase().includes(query) ||
            log.date.toLowerCase().includes(query) ||
            log.isoDate.includes(query)
        );
    }).sort((a, b) => b.isoDate.localeCompare(a.isoDate));

    // Month bounds: contract start month → current month.
    const minMonth = contractStartIso ? contractStartIso.slice(0, 7) : thisMonth;
    const maxMonth = thisMonth;
    const shiftMonth = (ym, delta) => {
        const [y, m] = ym.split('-').map(Number);
        const d = new Date(y, (m - 1) + delta, 1);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    };
    const monthLabel = (ym) => {
        const [y, m] = ym.split('-').map(Number);
        return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };
    // When searching, show matches across the loaded months; otherwise the
    // chosen month's rows (fetched on demand by the effect below).
    const visibleLogs = searchQuery.trim()
        ? filteredLogs
        : (monthCache[selectedMonth] || []);

    // Ray-cast point-in-polygon to flag whether the machine is inside the site.
    const pointInPolygon = (lat, lng, poly) => {
        if (!Array.isArray(poly) || poly.length < 3) return null;
        let inside = false;
        for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
            const xi = poly[i].lng, yi = poly[i].lat, xj = poly[j].lng, yj = poly[j].lat;
            const hit = ((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / ((yj - yi) || 1e-12) + xi);
            if (hit) inside = !inside;
        }
        return inside;
    };
    const insideGeofence = (geofence && rentalData?.machine?.location)
        ? pointInPolygon(rentalData.machine.location.lat, rentalData.machine.location.lng, geofence)
        : null;

    // --- 2. Data Fetching Logic ---
    // Base data (identity + manual logs) loads once; each MONTH of daily
    // stats is fetched when the user lands on it and cached after that.
    useEffect(() => {
        const fetchBase = async () => {
            try {
                const [detailsRes, manualRes] = await Promise.all([
                    rentalService.getRentalDetail(rentalId, contractId),
                    apiClient.get(`/manual-logs/${rentalId}`)
                ]);

                // --- 3. Data Transformation (ViewModel Creation) ---
                const data = detailsRes.data || detailsRes;

                setRentalData({
                    id: data.identity.equipment_id,
                    machine: {
                        name: data.identity.name,
                        category: data.identity.category,
                        image: '/images/mewp_machine.png',
                        status: data.status.current,
                        fuel: data.telemetry.fuel_level ? `${data.telemetry.fuel_level}%` : '-',
                        location: {
                            lat: data.telemetry.latitude || 12.9716, // Default to Bangalore if missing
                            lng: data.telemetry.longitude || 77.5946
                        }
                    },
                    customer: {
                        name: data.contract.contact_person,
                        company: data.contract.customer,
                        location: data.contract.location,
                        contact: data.contract.contact_phone
                    },
                    contract: {
                        id: data.contract.id,
                        startDate: data.contract.start_date !== '-' ? new Date(data.contract.start_date).toLocaleDateString() : '-',
                        endDate: data.contract.end_date !== '-' ? new Date(data.contract.end_date).toLocaleDateString() : 'Open-end',
                        status: data.contract.status || (contractId ? 'Completed' : 'Active')
                    }
                });

                const rawStartDateStr = data.contract.start_date;
                if (rawStartDateStr && rawStartDateStr !== '-') {
                    const sd = new Date(rawStartDateStr);
                    if (!isNaN(sd)) setContractStartIso(sd.toLocaleDateString('en-CA'));
                }

                const manualLogsData = manualRes.data || manualRes || [];
                const byDate = new Map();
                manualLogsData.forEach(log => {
                    if (!log.log_date) return;
                    const dStr = new Date(log.log_date).toLocaleDateString('en-CA');
                    byDate.set(dStr, log);
                });
                setManualByDate(byDate);
            } catch (error) {
                console.error("Failed to fetch rental details:", error);
                setManualByDate(new Map());
            } finally {
                setLoading(false);
            }
        };

        if (rentalId) fetchBase();
    }, [rentalId, contractId]);

    // --- 2b. One month of daily stats, on demand, cached. ---
    useEffect(() => {
        if (!rentalId || manualByDate === null) return;      // wait for base data
        if (monthCache[selectedMonth]) return;               // already loaded
        let alive = true;
        const fetchMonth = async () => {
            setMonthLoading(true);
            try {
                const summaryRes = await rentalService.getDailySummary(rentalId, selectedMonth);
                const summaryData = summaryRes.data || summaryRes;
                const backendLogs = (summaryData.logs || []);
                const backendLogsByDate = new Map();
                backendLogs.forEach(l => backendLogsByDate.set(l.date, l));

                const startDate = contractStartIso ? new Date(contractStartIso + 'T00:00:00') : null;
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                // The month's day window: contract start and today clip it.
                const [yy, mm] = selectedMonth.split('-').map(Number);
                const monthStart = new Date(yy, mm - 1, 1);
                const monthEnd = new Date(yy, mm, 0);
                const lo = startDate && startDate > monthStart ? startDate : monthStart;
                const hi = monthEnd < today ? monthEnd : today;

                const rows = [];
                if (startDate) {
                    for (let d = new Date(hi); d >= lo; d.setDate(d.getDate() - 1)) {
                        const dateObj = new Date(d);
                        const isoDate = dateObj.toLocaleDateString('en-CA');
                        const dateStr = dateObj.toLocaleDateString();
                        const dayNumber = Math.floor((dateObj.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

                        const bLog = backendLogsByDate.get(isoDate);
                        const manual = manualByDate.get(isoDate);

                        let opHours = 0;
                        let engineHours = 0;
                        let fuel = 0;
                        let firstOn = null;
                        let lastOff = null;

                        // 1. Backend Automated Data carries ultimate precedence
                        if (bLog) {
                            opHours = parseFloat(bLog.working_hours) || 0;
                            engineHours = parseFloat(bLog.engine_hours) || 0;
                            fuel = parseFloat(bLog.fuel_used_l ?? bLog.fuel_consumed) || 0; // litres (tank-capacity converted)
                            if (bLog.first_time && bLog.first_time !== '-') firstOn = bLog.first_time.slice(0, 5); // Trim to HH:MM format
                            if (bLog.last_time && bLog.last_time !== '-') lastOff = bLog.last_time.slice(0, 5);
                        }
                        // 2. Manual Data Fallback
                        else if (manual) {
                            if (manual.first_key_on && manual.last_key_off) {
                                const [h1, m1, s1 = 0] = manual.first_key_on.split(':').map(Number);
                                const [h2, m2, s2 = 0] = manual.last_key_off.split(':').map(Number);
                                const t1 = h1 + (m1 / 60) + (s1 / 3600);
                                const t2 = h2 + (m2 / 60) + (s2 / 3600);
                                opHours = Math.max(0, t2 - t1);
                            } else {
                                opHours = parseFloat(manual.engine_hours || 0);
                            }
                            engineHours = parseFloat(manual.engine_hours || 0);
                            fuel = parseFloat(manual.fuel_used || 0);
                            if (manual.first_key_on) firstOn = manual.first_key_on.slice(0, 5);
                            if (manual.last_key_off) lastOff = manual.last_key_off.slice(0, 5);
                        }

                        rows.push({
                            day: dateObj.toLocaleDateString('en-US', { weekday: 'long' }),
                            date: dateStr,
                            isoDate: isoDate,
                            dayNumber: dayNumber,
                            firstOn: firstOn,
                            lastOff: lastOff,
                            hours: {
                                operational: opHours,
                                engine: engineHours
                            },
                            fuel: fuel,
                            distance: 0,
                            hasAlarms: !!manual?.alert_type,
                            status: bLog ? 'Recorded' : (manual ? 'Manual Entry' : 'No Data'),
                            isManual: !bLog && !!manual
                        });
                    }
                }

                if (alive) setMonthCache(prev => ({ ...prev, [selectedMonth]: rows }));
            } catch (error) {
                console.error(`Failed to fetch ${selectedMonth} daily stats:`, error);
                if (alive) setMonthCache(prev => ({ ...prev, [selectedMonth]: [] }));
            } finally {
                if (alive) setMonthLoading(false);
            }
        };
        fetchMonth();
        return () => { alive = false; };
    }, [rentalId, selectedMonth, manualByDate, contractStartIso, monthCache]);

    // --- 4b. Fetch City Name based on Location ---
    useEffect(() => {
        if (rentalData?.machine?.location) {
            const fetchCity = async () => {
                try {
                    const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${rentalData.machine.location.lat}&lon=${rentalData.machine.location.lng}`);
                    const data = await res.json();
                    if (data && data.address) {
                        const city = data.address.city || data.address.town || data.address.village || data.address.state_district || data.address.county || 'Unknown Location';
                        setCityName(city);
                    }
                } catch (e) {
                    console.error("Failed to fetch city name", e);
                }
            };
            fetchCity();
        }
    }, [rentalData?.machine?.location?.lat, rentalData?.machine?.location?.lng]);

    // Load the contract's saved geofence polygon (marked site boundary).
    useEffect(() => {
        if (!contractId) return;
        let cancelled = false;
        geofenceService.getGeofence(contractId)
            .then(res => {
                const r = res?.data || res || {};
                const poly = r.geofence_polygon || r.polygon || r.geofence || (Array.isArray(r) ? r : null);
                const arr = (typeof poly === 'string') ? JSON.parse(poly) : poly;
                if (!cancelled && Array.isArray(arr) && arr.length >= 3) {
                    setGeofence(arr.map(p => ({ lat: Number(p.lat ?? p.latitude), lng: Number(p.lng ?? p.lon ?? p.longitude) }))
                        .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng)));
                }
            })
            .catch(() => { /* no geofence configured for this contract */ });
        return () => { cancelled = true; };
    }, [contractId]);

    // --- 5. Event Handlers ---
    // Per-machine usage report (this device only): open the date-range dialog,
    // pre-filled with the month currently being viewed.
    const openDownloadDialog = (format) => {
        setDlFormat(format);
        setDlError('');
        // Default range = the selected month (1st → today or month end).
        const first = `${selectedMonth}-01`;
        const [y, m] = selectedMonth.split('-').map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        const monthEnd = `${selectedMonth}-${String(lastDay).padStart(2, '0')}`;
        setDlFrom(first);
        setDlTo(monthEnd > thisMonth + '-31' ? new Date().toLocaleDateString('en-CA') : monthEnd);
        setDlOpen(true);
    };
    const handleDownloadPDF = () => openDownloadDialog('pdf');
    const handleDownloadExcel = () => openDownloadDialog('excel');

    const handleConfirmDownload = () => {
        if (!dlFrom || !dlTo) { setDlError('Please choose both From and To dates.'); return; }
        if (dlFrom > dlTo) { setDlError('"From" date must be on or before "To" date.'); return; }
        const baseUrl = process.env.REACT_APP_API_URL || 'https://api.mtandtiot.in/api';
        const url = `${baseUrl}/downloads/device-summary/${dlFormat}/${encodeURIComponent(rentalId)}?from=${dlFrom}&to=${dlTo}`;
        window.open(url, '_blank');
        setDlOpen(false);
    };


    if (loading) {
        return <Box p={3}><Skeleton variant="rectangular" height={400} sx={{ borderRadius: '24px' }} /></Box>;
    }

    if (!rentalData) {
        return <Box p={3}><Typography>No data found for this rental.</Typography></Box>;
    }

    // --- 7. Main Render ---
    return (
        <Box p={3} className="animate-fade-in">
            {/* Breadcrumbs */}
            <Breadcrumbs separator={<ChevronRight fontSize="small" />} sx={{ mb: 3 }}>
                <MuiLink component="button" onClick={() => navigate(origin.path)} underline="hover" color="inherit" sx={{ fontWeight: 600 }}>{origin.label}</MuiLink>
                {contractId && (
                    <MuiLink
                        component="button"
                        onClick={() => navigate(`/contract-live/${contractId}`)}
                        underline="hover"
                        color="inherit"
                        sx={{ fontWeight: 600 }}
                    >
                        {contractId}
                    </MuiLink>
                )}
                <Typography color="text.primary" sx={{ fontWeight: 700 }}>{rentalData.id}</Typography>
            </Breadcrumbs>

            {/* Top Box: Machine & Contract Details */}
            <Card sx={{ mb: 4, borderRadius: '24px', overflow: 'visible' }}>
                <CardContent sx={{ p: '24px !important' }}>
                    {/* Single Row Layout: Optimized for "All in one line" */}
                    <Box display="flex" alignItems="center" width="100%" sx={{ gap: 3 }}>

                        {/* 1. Machine Identity */}
                        <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 280 }}>
                            <Box
                                sx={{
                                    width: 64, // Reduced from 80
                                    height: 64, // Reduced from 80
                                    bgcolor: 'grey.50',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 1,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                <Box component="img" src={rentalData.machine.image} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => { e.target.src = "https://via.placeholder.com/200?text=UNIT"; }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{rentalData.machine.name}</Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>{rentalData.machine.category}</Typography>
                                <Chip
                                    label={rentalData.contract.status === 'COMPLETED' ? 'Completed' : rentalData.machine.status}
                                    color={rentalData.contract.status === 'COMPLETED' ? 'default' : 'primary'}
                                    size="small"
                                    sx={{ fontWeight: 800, borderRadius: '6px', height: '22px', fontSize: '11px' }}
                                />
                            </Box>
                        </Box>

                        {/* Thin vertical separator */}
                        <Divider orientation="vertical" flexItem sx={{ opacity: 0.6 }} />

                        {/* 2. Contract Stats Row (Flexible Grid) */}
                        <Box flexGrow={1} display="flex" alignItems="center" justifyContent="space-between" sx={{ gap: 2 }}>

                            {/* Assigned Date */}
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Avatar sx={{ bgcolor: 'warning.50', color: 'warning.main', borderRadius: '8px', width: 36, height: 36 }} variant="rounded">
                                    <Event sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">ASSIGNED DATE</Typography>
                                    <Typography variant="subtitle2" fontWeight={800}>{rentalData.contract.startDate}</Typography>
                                </Box>
                            </Box>

                            {/* Assigned To */}
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Avatar sx={{ bgcolor: 'primary.50', color: 'primary.main', borderRadius: '8px', width: 36, height: 36 }} variant="rounded">
                                    <Person sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">ASSIGNED TO</Typography>
                                    <Typography variant="subtitle2" fontWeight={800}>{rentalData.customer.name}</Typography>
                                    <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>{rentalData.customer.company}</Typography>
                                </Box>
                            </Box>

                            {/* Contact Phone */}
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Avatar sx={{ bgcolor: 'info.50', color: 'info.main', borderRadius: '8px', width: 36, height: 36 }} variant="rounded">
                                    <Person sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">CONTACT INFO</Typography>
                                    <Typography variant="subtitle2" fontWeight={800}>{rentalData.customer.contact || "-"}</Typography>
                                </Box>
                            </Box>

                            {/* Location (Truncated) */}
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Avatar sx={{ bgcolor: 'error.50', color: 'error.main', borderRadius: '8px', width: 36, height: 36 }} variant="rounded">
                                    <LocationOn sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">LOCATION</Typography>
                                    <Tooltip title={rentalData.customer.location}>
                                        <Typography variant="subtitle2" fontWeight={800} sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {rentalData.customer.location}
                                        </Typography>
                                    </Tooltip>
                                </Box>
                            </Box>

                            {/* Start Date */}
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Avatar sx={{ bgcolor: 'success.50', color: 'success.main', borderRadius: '8px', width: 36, height: 36 }} variant="rounded">
                                    <CalendarToday sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">START DATE</Typography>
                                    <Typography variant="subtitle2" fontWeight={800}>{rentalData.contract.startDate}</Typography>
                                </Box>
                            </Box>

                        </Box>

                        {/* 3. Actions Group */}
                        <Box display="flex" alignItems="center" gap={1} sx={{ pl: 2, borderLeft: 1, borderColor: 'divider' }}>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<Description sx={{ fontSize: 18 }} />}
                                onClick={() => {
                                    const contractId = rentalData.contract.id && rentalData.contract.id !== '-' ? rentalData.contract.id : rentalId;
                                    navigate(`/contract/${contractId}`);
                                }}
                                sx={{
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    borderRadius: '10px',
                                    fontWeight: 700,
                                    px: 2,
                                    py: 1,
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        bgcolor: 'primary.dark'
                                    }
                                }}
                            >
                                View Contract
                            </Button>

                            <Stack spacing={0.5}>
                                <IconButton
                                    onClick={handleDownloadPDF}
                                    size="small"
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: '6px',
                                        p: 0.5,
                                        width: 28,
                                        height: 28
                                    }}
                                    title="Download PDF"
                                >
                                    <PictureAsPdf sx={{ fontSize: 16, color: 'error.main' }} />
                                </IconButton>
                                <IconButton
                                    onClick={handleDownloadExcel}
                                    size="small"
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: '6px',
                                        p: 0.5,
                                        width: 28,
                                        height: 28
                                    }}
                                    title="Download Excel"
                                >
                                    <TableChart sx={{ fontSize: 16, color: 'success.main' }} />
                                </IconButton>
                            </Stack>
                        </Box>

                    </Box>
                </CardContent>
            </Card>


            {/* ============================================== */}
            {/* NEW 50/50 SPLIT LAYOUT - MAP & USAGE LOGS */}
            {/* ============================================== */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 3,
                    width: '100%',
                }}
            >
                {/* LEFT PANEL: LIVE LOCATION MAP (50%) */}
                <Box
                    sx={{
                        flex: '1 1 50%',
                        minWidth: 0,
                    }}
                >
                    <Card
                        sx={{
                            borderRadius: '24px',
                            overflow: 'hidden',
                            height: 500,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Map Header */}
                        <Box
                            sx={{
                                p: 2,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                bgcolor: 'grey.50',
                                flexShrink: 0,
                            }}
                        >
                            <Avatar
                                sx={{
                                    bgcolor: 'white',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    width: 32,
                                    height: 32,
                                }}
                            >
                                <MapIcon sx={{ fontSize: 18, color: 'text.primary' }} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                                Live Location
                                {cityName && (
                                    <Chip
                                        label={cityName}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontWeight: 800, ml: 1.5, height: '22px', fontSize: '11px', bgcolor: 'primary.50' }}
                                    />
                                )}
                                {/* Inside / outside the marked site geofence */}
                                {insideGeofence !== null && (
                                    <Chip
                                        label={insideGeofence ? 'Inside site' : 'Outside site'}
                                        size="small"
                                        sx={{ fontWeight: 800, height: '22px', fontSize: '11px',
                                            bgcolor: insideGeofence ? '#dcfce7' : '#fee2e2',
                                            color: insideGeofence ? '#15803d' : '#b91c1c' }}
                                    />
                                )}
                            </Typography>
                        </Box>

                        {/* Map Container */}
                        <Box
                            sx={{
                                flexGrow: 1,
                                position: 'relative',
                                '& .leaflet-container img': {
                                    maxWidth: 'none !important',
                                    maxHeight: 'none !important',
                                    display: 'block',
                                },
                            }}
                        >
                            <MapContainer
                                center={[rentalData.machine.location.lat, rentalData.machine.location.lng]}
                                zoom={13}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <Marker position={[rentalData.machine.location.lat, rentalData.machine.location.lng]}>
                                    <Popup>
                                        <b>{rentalData.machine.name}</b>
                                        <br />
                                        {rentalData.customer.location}
                                    </Popup>
                                </Marker>
                                {/* Marked site geofence — green when the machine is inside, red when outside */}
                                {geofence && geofence.length >= 3 && (
                                    <Polygon
                                        positions={geofence.map(p => [p.lat, p.lng])}
                                        pathOptions={{
                                            color: insideGeofence === false ? '#ef4444' : '#10b981',
                                            fillColor: insideGeofence === false ? '#ef4444' : '#10b981',
                                            fillOpacity: 0.12,
                                            weight: 2,
                                        }}
                                    />
                                )}
                            </MapContainer>
                        </Box>
                    </Card>
                </Box>

                {/* RIGHT PANEL: USAGE LOGS (50%) */}
                <Box
                    sx={{
                        flex: '1 1 50%',
                        minWidth: 0,
                    }}
                >
                    <Card
                        sx={{
                            borderRadius: '24px',
                            overflow: 'hidden',
                            height: 500,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Logs Header with Search */}
                        <Box
                            sx={{
                                p: 2,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                bgcolor: 'grey.50',
                                flexShrink: 0,
                            }}
                        >
                            <Typography variant="h6" fontWeight={800}>
                                Usage Logs
                            </Typography>

                            {/* Month selector — prev / month picker / next, bounded
                                to [contract start month, current month]. Hidden while searching. */}
                            {!isSearchOpen && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                    <Tooltip title="Previous month">
                                        <span>
                                            <IconButton size="small" disabled={selectedMonth <= minMonth}
                                                onClick={() => setSelectedMonth(m => shiftMonth(m, -1))}>
                                                <ChevronLeft fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <TextField
                                        type="month" size="small" value={selectedMonth}
                                        onChange={(e) => e.target.value && setSelectedMonth(e.target.value)}
                                        inputProps={{ min: minMonth, max: maxMonth }}
                                        sx={{ width: 145, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' }, '& input': { fontWeight: 700, fontSize: '0.82rem', py: 0.75 } }}
                                    />
                                    <Tooltip title="Next month">
                                        <span>
                                            <IconButton size="small" disabled={selectedMonth >= maxMonth}
                                                onClick={() => setSelectedMonth(m => shiftMonth(m, 1))}>
                                                <ChevronRight fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            )}

                            {/* Search Section */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {isSearchOpen ? (
                                    <TextField
                                        size="small"
                                        placeholder="Search day or date..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        autoFocus
                                        sx={{
                                            width: 180,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                bgcolor: 'white',
                                                '& fieldset': {
                                                    borderColor: 'divider',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'primary.main',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'primary.main',
                                                },
                                            },
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: searchQuery && (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setSearchQuery('')}
                                                        sx={{ p: 0.25 }}
                                                    >
                                                        <Close sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                ) : null}
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setIsSearchOpen(!isSearchOpen);
                                        if (isSearchOpen) setSearchQuery('');
                                    }}
                                    sx={{
                                        bgcolor: isSearchOpen ? 'primary.main' : 'white',
                                        color: isSearchOpen ? 'white' : 'text.secondary',
                                        border: '1px solid',
                                        borderColor: isSearchOpen ? 'primary.main' : 'divider',
                                        borderRadius: '10px',
                                        '&:hover': {
                                            bgcolor: isSearchOpen ? 'primary.dark' : 'grey.100',
                                        },
                                    }}
                                >
                                    {isSearchOpen ? <Close sx={{ fontSize: 18 }} /> : <Search sx={{ fontSize: 18 }} />}
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Scrollable Logs List */}
                        <Box
                            sx={{
                                flexGrow: 1,
                                overflowY: 'auto',
                                p: 2,
                            }}
                        >
                            <Stack spacing={2}>
                                {/* The chosen month is fetched on demand — skeletons while it loads. */}
                                {monthLoading && !searchQuery.trim() && visibleLogs.length === 0 ? (
                                    [1, 2, 3].map(i => (
                                        <Card key={`sk-${i}`} sx={{ borderRadius: '16px', p: 2 }}>
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: 'action.hover' }} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Box sx={{ height: 14, width: '35%', bgcolor: 'action.hover', borderRadius: 1, mb: 1 }} />
                                                    <Box sx={{ height: 10, width: '55%', bgcolor: 'action.hover', borderRadius: 1 }} />
                                                </Box>
                                            </Stack>
                                        </Card>
                                    ))
                                ) : visibleLogs.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography color="text.secondary" fontWeight={600}>
                                            {searchQuery.trim()
                                                ? `No logs found for "${searchQuery}"`
                                                : `No usage logs in ${monthLabel(selectedMonth)}`}
                                        </Typography>
                                    </Box>
                                ) : null}
                                {visibleLogs.map((log, index) => (
                                    <Card
                                        key={index}
                                        onClick={() => navigate(`/rental-log/${rentalData.id}?date=${log.isoDate}${rentalData?.contract?.id && rentalData.contract.id !== '-' ? `&contractId=${rentalData.contract.id}` : ''}`)}
                                        sx={{
                                            borderRadius: '16px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            border: '1px solid',
                                            borderColor: log.isManual ? '#f59e0b' : (log.status === 'Active' ? 'primary.200' : 'divider'),
                                            bgcolor: log.isManual ? '#fffbeb' : (log.status === 'Active' ? 'primary.50' : 'white'),
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 8px 20px rgba(0,0,0,0.05)',
                                                borderColor: log.isManual ? '#d97706' : 'primary.main',
                                            },
                                        }}

                                    >
                                        <CardContent sx={{ p: '16px !important' }}>
                                            {/* Log Header Row */}
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                                <Stack direction="row" alignItems="center" gap={2}>
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: log.isManual ? '#f59e0b' : (log.status === 'Active' || log.status === 'Recorded' ? 'primary.main' : 'grey.100'),
                                                            color: log.isManual || log.status === 'Active' || log.status === 'Recorded' ? 'white' : 'grey.600',
                                                            fontWeight: 700,
                                                            borderRadius: '12px',
                                                            width: 40,
                                                            height: 40,
                                                        }}
                                                    >
                                                        {log.dayNumber}
                                                    </Avatar>
                                                    <Box>
                                                        <Stack direction="row" alignItems="center" gap={1}>
                                                            <Typography variant="subtitle1" fontWeight={800}>
                                                                {log.day}
                                                            </Typography>
                                                            {log.isManual && (
                                                                <Chip
                                                                    label="Manual Data"
                                                                    size="small"
                                                                    sx={{
                                                                        height: 18,
                                                                        fontSize: '10px',
                                                                        fontWeight: 800,
                                                                        bgcolor: '#fcd34d',
                                                                        color: '#92400e',
                                                                        borderRadius: '4px'
                                                                    }}
                                                                />
                                                            )}
                                                        </Stack>

                                                        <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            {log.date}
                                                            {log.firstOn && (
                                                                <>
                                                                    <Typography component="span" variant="caption" sx={{ color: 'primary.main', fontWeight: 800, bgcolor: 'primary.50', px: 1, borderRadius: '4px' }}>
                                                                        ON: {log.firstOn}
                                                                    </Typography>
                                                                    <Typography component="span" variant="caption" sx={{ color: 'error.main', fontWeight: 800, bgcolor: 'error.50', px: 1, borderRadius: '4px' }}>
                                                                        OFF: {log.lastOff}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                                <IconButton size="small">
                                                    <ArrowForward />
                                                </IconButton>
                                            </Box>

                                            {/* Stats Row */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    pt: 1.5,
                                                    borderTop: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                {/* Working Hours */}
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Stack direction="row" alignItems="center" gap={0.5} justifyContent="center">
                                                        <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                            WORKING
                                                        </Typography>
                                                    </Stack>
                                                    <Typography variant="subtitle1" fontWeight={800} title="Wallclock hours from First ON to Last OFF">
                                                        {log.hours.operational.toFixed(1)}h
                                                    </Typography>
                                                </Box>

                                                {/* Engine Hours */}
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Stack direction="row" alignItems="center" gap={0.5} justifyContent="center">
                                                        <Speed sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                            ENGINE
                                                        </Typography>
                                                    </Stack>
                                                    <Typography variant="subtitle1" fontWeight={800} title="Actual Engine Running Time (Meter delta)">
                                                        {log.hours.engine.toFixed(1)}h
                                                    </Typography>
                                                </Box>

                                                {/* Fuel */}
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Stack direction="row" alignItems="center" gap={0.5} justifyContent="center">
                                                        <LocalGasStation sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                            FUEL USED
                                                        </Typography>
                                                    </Stack>
                                                    <Typography variant="subtitle1" fontWeight={800}>
                                                        {log.fuel > 0 ? `${log.fuel.toFixed(1)} L` : '-'}
                                                    </Typography>
                                                </Box>

                                                {/* Alarms */}
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Stack direction="row" alignItems="center" gap={0.5} justifyContent="center">
                                                        <Box
                                                            sx={{
                                                                width: 14,
                                                                height: 14,
                                                                borderRadius: '50%',
                                                                bgcolor: log.hasAlarms ? 'error.main' : 'success.main',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            {log.hasAlarms ? (
                                                                <WarningAmber sx={{ fontSize: 8, color: 'white' }} />
                                                            ) : (
                                                                <CheckCircleOutline sx={{ fontSize: 8, color: 'white' }} />
                                                            )}
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                            ALARMS
                                                        </Typography>
                                                    </Stack>
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight={800}
                                                        color={log.hasAlarms ? 'error.main' : 'success.main'}
                                                    >
                                                        {log.hasAlarms ? 'Yes' : 'No'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        </Box>
                    </Card>
                </Box>
            </Box>

            {/* Download date-range dialog (PDF / Excel for this machine) */}
            <Dialog open={dlOpen} onClose={() => setDlOpen(false)} PaperProps={{ sx: { borderRadius: '16px', minWidth: 420 } }}>
                <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {dlFormat === 'excel'
                        ? <TableChart sx={{ color: '#16a34a' }} />
                        : <PictureAsPdf sx={{ color: '#dc2626' }} />}
                    Download {dlFormat === 'excel' ? 'Excel' : 'PDF'} — {rentalData?.machine?.name || rentalId}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Choose the date range for this machine's usage report.
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <TextField label="From" type="date" value={dlFrom} fullWidth
                            onChange={(e) => setDlFrom(e.target.value)} InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                        <TextField label="To" type="date" value={dlTo} fullWidth
                            onChange={(e) => setDlTo(e.target.value)} InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                    </Stack>
                    {dlError && <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>{dlError}</Alert>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDlOpen(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>Cancel</Button>
                    <Button variant="contained" onClick={handleConfirmDownload} startIcon={<DownloadIcon />}
                        disabled={!dlFrom || !dlTo}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px' }}>
                        Download
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RentalDetailsPage;
